import { useState } from 'react';
import { toast } from 'sonner';
import { Pencil } from 'lucide-react';
import { useAuth } from '@/contexts/useAuth';
import { updateProfile } from '@/lib/api/profile';
import { track } from '@/lib/analytics';
import type { FriendlyError } from '@/lib/errors';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import ErrorAlert from '@/components/ui/ErrorAlert';

export default function EditableNameRow() {
  const { profile, user, refreshProfile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(profile?.full_name ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<FriendlyError | null>(null);

  const initial = profile?.full_name?.charAt(0)?.toUpperCase() ?? '?';
  const trimmed = draft.trim();
  const unchanged = trimmed === (profile?.full_name ?? '');
  const canSave = !!trimmed && !unchanged && !saving;

  const startEdit = () => {
    setDraft(profile?.full_name ?? '');
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    setDraft(profile?.full_name ?? '');
    setError(null);
    setEditing(false);
  };

  const save = async () => {
    if (!user?.id || !canSave) return;
    setSaving(true);
    setError(null);
    const result = await updateProfile(user.id, { full_name: trimmed });
    if (!result.success) {
      setError(result.error ?? null);
      setSaving(false);
      return;
    }
    await refreshProfile();
    track('profile_updated', { field: 'name' });
    setSaving(false);
    setEditing(false);
    toast.success('Saved');
  };

  return (
    <Card>
      <div className="flex items-center gap-4 md:gap-6">
        <div className="w-14 h-14 md:w-20 md:h-20 rounded-full bg-primary-light flex items-center justify-center flex-shrink-0">
          <span className="font-display font-semibold text-xl md:text-3xl text-primary">
            {initial}
          </span>
        </div>

        <div className="min-w-0 flex-1">
          {!editing ? (
            <>
              <p className="font-sans font-semibold text-base md:text-xl text-text-primary truncate">
                {profile?.full_name ?? 'User'}
              </p>
              <p className="font-sans text-sm md:text-base text-text-muted truncate">
                {user?.email ?? profile?.email ?? 'No email'}
              </p>
            </>
          ) : (
            <Input
              value={draft}
              onChange={setDraft}
              maxLength={50}
              autoFocus
              placeholder="Your name"
            />
          )}
        </div>

        {!editing && (
          <button
            type="button"
            onClick={startEdit}
            aria-label="Edit name"
            className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full text-text-secondary hover:bg-cream-warm transition-colors cursor-pointer"
          >
            <Pencil size={16} />
          </button>
        )}
      </div>

      {editing && (
        <>
          {error && <ErrorAlert error={error} className="mt-4" />}
          <div className="mt-4 flex gap-3">
            <Button variant="ghost" onClick={cancel}>
              Cancel
            </Button>
            <div className="flex-1">
              <Button fullWidth onClick={save} disabled={!canSave} loading={saving}>
                Save
              </Button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
